import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import { Observable, map } from 'rxjs';
import { VIRTUAL_ASSISTANT } from 'src/config/constant';
import { environment } from '../../../../environments/environment';
import type {
  AssistantChatData,
  AssistantChatRequest,
  ChatStreamEvent,
} from '../../../features/assistant/voice/types/voice-assistant.types';

/** Raw API body (may be envelope or flat). */
interface AssistantChatApiShape {
  status?: number;
  message?: string;
  data?: AssistantChatData;
  response?: string;
  speechText?: string;
  action?: AssistantChatData['action'];
  metadata?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class VirtualAssistantService {
  private apiService = inject(ApiService);

  constructor() {}

  /**
   * Structured assistant chat (voice / quick commands). Requires teacher JWT.
   */
  chat(payload: AssistantChatRequest): Observable<AssistantChatData> {
    return this.apiService
      .post<AssistantChatApiShape, AssistantChatRequest>(
        VIRTUAL_ASSISTANT.CHAT.URL,
        payload
      )
      .pipe(map(res => this.unwrapChatResponse(res)));
  }

  /**
   * SSE stream with per-event callback (sentence + done).
   */
  chatStream(
    payload: AssistantChatRequest,
    onEvent: (evt: ChatStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<AssistantChatData> {
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const url = `${environment.apiUrl}/${VIRTUAL_ASSISTANT.CHAT.STREAM_URL}`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal,
    }).then(async res => {
      if (!res.ok || !res.body) {
        const bodyText = await res.text().catch(() => '');
        let message = `Stream request failed (${res.status})`;
        try {
          const parsed = JSON.parse(bodyText) as { message?: string };
          if (typeof parsed.message === 'string' && parsed.message.trim()) {
            message = parsed.message.trim();
          }
        } catch {
          if (bodyText.trim()) {
            message = bodyText.trim();
          }
        }
        const err = new Error(message);
        (err as Error & { status?: number }).status = res.status;
        throw err;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalData: AssistantChatData | null = null;
      let streamError: string | null = null;

      const parseBlock = (block: string) => {
        const lines = block.split('\n');
        let eventName = 'message';
        let dataLine = '';
        for (const line of lines) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
        }
        if (!dataLine) return;
        let parsed: { text?: string; message?: string };
        try {
          parsed = JSON.parse(dataLine);
        } catch {
          return;
        }
        if (eventName === 'sentence' && typeof parsed.text === 'string') {
          onEvent({ type: 'sentence', text: parsed.text });
        } else if (eventName === 'done') {
          const data = this.unwrapChatResponse(parsed as AssistantChatApiShape);
          finalData = data;
          onEvent({ type: 'done', data });
        } else if (eventName === 'error') {
          streamError = String(parsed.message || 'Stream error');
          onEvent({
            type: 'error',
            message: streamError,
          });
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          if (part.trim()) parseBlock(part);
        }
      }

      if (!finalData) {
        throw new Error(streamError ?? 'Stream ended without done event');
      }
      return finalData;
    });
  }

  private unwrapChatResponse(res: AssistantChatApiShape): AssistantChatData {
    const data = (res?.data ?? res) as AssistantChatData & AssistantChatApiShape;
    const responseText =
      typeof data?.response === 'string'
        ? data.response
        : typeof (data as AssistantChatApiShape)?.response === 'string'
          ? (data as AssistantChatApiShape).response!
          : '';
    const speechText =
      typeof data?.speechText === 'string'
        ? data.speechText
        : typeof (data as AssistantChatApiShape)?.speechText === 'string'
          ? (data as AssistantChatApiShape).speechText
          : undefined;
    return {
      response: responseText,
      speechText,
      action: data?.action ?? null,
      metadata:
        data?.metadata && typeof data.metadata === 'object'
          ? (data.metadata as Record<string, unknown>)
          : {},
    };
  }

  /**
   * Generate educational content using AI
   * @param payload - domain, questionContent, pedagogy, level, artifact, etc. (matches backend Zod)
   * @returns An observable containing the generated content
   */
  generateEducationalContent(payload: {
    domainName: string;
    questionContent: string;
    pedagogy: string;
    level: string;
    artifact: string;
    numberOfSuggestions: number;
    country?: string;
    language?: string;
    includeLocalContext?: boolean;
    teacherId?: number;
    schoolId?: number;
  }): Observable<object> {
    return this.apiService.post(VIRTUAL_ASSISTANT.GENERATE.URL, payload);
  }

  /** Lightweight creative generation for quick workflow mode. */
  generateQuickContent(payload: {
    artifact: string;
    prompt: string;
    language?: string;
    ageHint?: string;
  }): Observable<object> {
    return this.apiService.post(VIRTUAL_ASSISTANT.GENERATE_QUICK.URL, payload);
  }

  /**
   * Get teacher's generation history
   * @param teacherId - The teacher ID
   * @param limit - Number of records to fetch (default: 10)
   * @param offset - Offset for pagination (default: 0)
   * @returns An observable containing the history list
   */
  getTeacherHistory(artifact: any, teacherId: number, limit: number = 10, offset: number = 0): Observable<object> {
    return this.apiService.get(`${VIRTUAL_ASSISTANT.HISTORY.TEACHER.URL}/${teacherId}`, {}, {
      limit: limit.toString(),
      offset: offset.toString(),
      artifact: artifact
    });
  }

  /**
   * Get a specific generated response by ID
   * @param responseId - The response ID
   * @returns An observable containing the full response details
   */
  getResponseById(responseId: number): Observable<object> {
    return this.apiService.get(`${VIRTUAL_ASSISTANT.RESPONSE.BY_ID.URL}/${responseId}`);
  }

  /**
   * Get virtual assistant domains (root sections of the assessment)
   */
  getDomains(assessmentId: number, language: string): Observable<object> {
    return this.apiService.get(VIRTUAL_ASSISTANT.DOMAINS.URL, {}, { assessmentId, language });
  }

  /**
   * Get virtual assistant assessments
   */
  getAssessments(language?: string): Observable<object> {
    const params: any = {};
    if (language) params.language = language;
    return this.apiService.get(VIRTUAL_ASSISTANT.ASSESSMENTS.URL, {}, params);
  }

  /**
   * Get assessment hierarchy (sections and questions)
   */
  getAssessmentHierarchy(assessmentId: number, language?: string): Observable<object> {
    const params: any = {};
    if (language) params.language = language;
    return this.apiService.get(`${VIRTUAL_ASSISTANT.HIERARCHY.URL}/${assessmentId}/hierarchy`, {}, params);
  }

  /**
   * Get questions by section ID
   */
  getQuestionsBySection(sectionId: number, language?: string): Observable<object> {
    const params: any = {};
    if (language) params.language = language;
    return this.apiService.get(`${VIRTUAL_ASSISTANT.QUESTIONS.URL}/${sectionId}/questions`, {}, params);
  }
}
